/* LOGIN PAGE - Updated Brand Name to ToDo Toucan */
import React, { useState, useEffect } from 'react';
import { useStore } from '../store/StoreContext';
import { Input } from '../components/UI/Input';
import { Button } from '../components/UI/Button';
import { ImageUpload } from '../components/UI/ImageUpload';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase/supabase';

export const Login = () => {
  const { state, login, signup, resetPassword, updatePassword, clearRecoveryMode } = useStore();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    avatar: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const checkRecovery = async () => {
      if (state.isRecoveryMode) {
        setMode('update_password');
        return;
      }
      const hash = window.location.hash;
      if (hash.includes('type=recovery') || hash.includes('access_token=')) {
        setMode('update_password');
        const { data: { session } } = await supabase.auth.getSession();
      }
    };
    checkRecovery();
  }, [state.isRecoveryMode]);

  const logoUrl = "https://media-manager-c.questera.ai/greta-media/981ecb6892631c31e92fca6d10d0332603596e5d429fd99cee63046a83be378b4365dffb7696bc6c52f453f1ea7f8977/images/aW1hZ2UvcG5n/5cfcf8e4487f6f866c526cb09ffe7a05.png";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setIsProcessing(true);
    try {
      if (mode === 'login') {
        const { error: authError } = await login(formData.email, formData.password);
        if (authError) throw authError;
      } else if (mode === 'signup') {
        const { error: authError } = await signup(formData);
        if (authError) throw authError;
        setSuccessMsg('Account created! Please sign in.');
        setMode('login');
      } else if (mode === 'forgot') {
        const { error: authError } = await resetPassword(formData.email);
        if (authError) throw authError;
        setSuccessMsg('Check your email for the reset link!');
      } else if (mode === 'update_password') {
        if (formData.password !== formData.confirmPassword) {
          throw new Error("Passwords do not match");
        }
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error("Your reset session has expired. Please request a new link.");
        }
        await updatePassword(formData.password);
        setSuccessMsg('Password updated! Redirecting...');
        clearRecoveryMode();
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (err) {
      console.error("Auth error:", err);
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-base-900 bg-gantt-pattern relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/5 pointer-events-none" />
      <motion.div layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md p-8 bg-base-800/50 backdrop-blur-xl border border-base-700 rounded-3xl shadow-2xl relative z-10">
        <div className="flex flex-col items-center mb-8">
          <motion.div whileHover={{ scale: 1.05, rotate: 5 }} className="w-24 h-24 rounded-2xl overflow-hidden mb-6 border border-primary/30 shadow-2xl shadow-primary/20 bg-white">
            <img src={logoUrl} className="w-full h-full object-contain p-2" alt="ToDo Toucan Logo" />
          </motion.div>
          <h1 className="text-4xl font-black text-white tracking-tight">ToDo Toucan</h1>
          <p className="text-gray-400 mt-2 text-sm text-center font-medium">
            {mode === 'login' ? 'Welcome back to the canopy' : mode === 'signup' ? 'Start your nest' : mode === 'forgot' ? 'Find your way back' : 'Secure your nest'}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          <AnimatePresence mode="wait">
            {mode === 'signup' && (
              <motion.div key="signup-fields" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-5 overflow-hidden">
                <Input label="Full Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required disabled={isProcessing} />
                <ImageUpload label="Profile Picture" currentUrl={formData.avatar} onUpload={(url) => setFormData({ ...formData, avatar: url })} />
              </motion.div>
            )}
          </AnimatePresence>
          {(mode === 'login' || mode === 'signup' || mode === 'forgot') && (
            <Input label="Email Address" type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required placeholder="you@company.com" disabled={isProcessing || mode === 'update_password'} />
          )}
          {(mode === 'login' || mode === 'signup' || mode === 'update_password') && (
            <div className="relative">
              <Input label={mode === 'update_password' ? "New Password" : "Password"} type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} required placeholder="••••••••" disabled={isProcessing} />
              {mode === 'login' && (
                <button type="button" onClick={() => setMode('forgot')} className="absolute right-0 top-0 text-[10px] text-gray-500 hover:text-primary uppercase font-bold tracking-widest transition-colors">
                  Forgot?
                </button>
              )}
            </div>
          )}
          {mode === 'update_password' && (
            <Input label="Confirm New Password" type="password" value={formData.confirmPassword} onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })} required placeholder="••••••••" disabled={isProcessing} />
          )}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-xs flex items-start gap-2 animate-shake">
              <SafeIcon name="AlertCircle" className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          {successMsg && (
            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-xs flex items-start gap-2">
              <SafeIcon name="CheckCircle" className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{successMsg}</span>
            </div>
          )}
          <Button type="submit" variant="primary" className="w-full py-4 h-14 text-lg font-bold" disabled={isProcessing}>
            {isProcessing ? <SafeIcon name="Loader" className="w-5 h-5 animate-spin mr-2" /> : mode === 'login' ? 'Sign In' : mode === 'signup' ? 'Join the Flock' : mode === 'forgot' ? 'Send Reset Link' : 'Save New Password'}
          </Button>
        </form>
        <div className="mt-8 pt-6 border-t border-base-700 text-center">
          <button onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); setSuccessMsg(''); }} className="text-sm text-gray-400 hover:text-white transition-colors">
            {mode === 'login' ? "New here? " : mode === 'forgot' ? "Wait, I remember! " : "Back to "} <span className="text-primary font-bold">{mode === 'login' ? 'Create Account' : 'Log In'}</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};